/**
 * Iter 320av (Feb 2026) — Floating accessibility widget for pilgrim pages.
 *
 * Fixed bottom-right button (matches PWA install badge conventions). Opens
 * a popover with:
 *   • Font size picker (sm / md / lg / xl) — reuses existing preference
 *   • High-contrast toggle — boosts saturation, adds link underlines,
 *     strengthens text weight
 *
 * Both preferences persist via ClientPreferencesContext (localStorage).
 * Only rendered on the client side of the app (mounted at the client root).
 */
import React, { useState, useEffect, useRef } from 'react';
import { useClientPreferences } from '../context/ClientPreferencesContext';
import { Accessibility, Type, Contrast, X, Check } from 'lucide-react';

const FONT_LABELS = { sm: 'A-', md: 'A', lg: 'A+', xl: 'A++' };

const ClientA11yButton = () => {
  const { fontSize, setFontSize, highContrast, setHighContrast } = useClientPreferences();
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  // Close on outside click and Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
        <button
          ref={btnRef}
          type="button"
          onClick={() => setOpen(o => !o)}
          className="fixed bottom-[72px] sm:bottom-4 right-3 sm:right-4 z-[95] w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-demarre-800/95 hover:bg-demarre-900 text-white shadow-lg flex items-center justify-center transition-all hover:scale-105 focus-visible:ring-4 focus-visible:ring-amber-300 backdrop-blur-sm"
          style={{ marginBottom: 'env(safe-area-inset-bottom, 0)' }}
          aria-label="Tamanho da fonte e contraste"
          aria-expanded={open}
          aria-controls="a11y-popover"
          title="Tamanho da fonte e contraste"
          data-testid="a11y-fab"
        >
          {/* Iter 351 — "Aa" badge is clearer than the wheelchair-adjacent
              Accessibility icon for a font-size / contrast widget. */}
          <span aria-hidden="true" className="flex items-baseline leading-none" style={{ fontFamily: '"Fraunces", serif' }}>
            <span className="text-[22px] font-semibold">A</span>
            <span className="text-[13px] font-semibold -ml-[1px]">a</span>
          </span>
        </button>

      {open && (
        <div
          ref={popRef}
          id="a11y-popover"
          role="dialog"
          aria-label="Preferências de acessibilidade"
          className="fixed bottom-[130px] sm:bottom-20 right-3 sm:right-4 z-[96] w-[calc(100vw-24px)] max-w-xs sm:w-72 bg-white rounded-xl shadow-2xl border border-gray-100 p-4 animate-in fade-in slide-in-from-bottom-2 duration-150"
          data-testid="a11y-popover"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-demarre-900 text-sm flex items-center gap-2">
              <Accessibility className="w-4 h-4" />
              Acessibilidade
            </h3>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
              aria-label="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 mb-2">
                <Type className="w-3.5 h-3.5" />
                <span>Tamanho da fonte</span>
              </div>
              <div className="grid grid-cols-4 gap-1.5" role="radiogroup" aria-label="Tamanho da fonte">
                {(['sm', 'md', 'lg', 'xl']).map(sz => (
                  <button
                    key={sz}
                    type="button"
                    role="radio"
                    aria-checked={fontSize === sz}
                    onClick={() => setFontSize(sz)}
                    className={`h-11 rounded-lg border text-sm font-semibold transition-colors ${
                      fontSize === sz
                        ? 'bg-demarre-700 border-demarre-700 text-white'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-demarre-300'
                    }`}
                    data-testid={`a11y-font-${sz}`}
                    style={{ fontSize: sz === 'sm' ? '12px' : sz === 'md' ? '15px' : sz === 'lg' ? '18px' : '21px' }}
                  >
                    {FONT_LABELS[sz]}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5">Aumenta os textos em todo o portal.</p>
            </div>

            <div className="pt-3 border-t">
              <label className="flex items-start gap-3 cursor-pointer group">
                <button
                  type="button"
                  role="switch"
                  aria-checked={highContrast}
                  onClick={() => setHighContrast(!highContrast)}
                  className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 mt-0.5 ${highContrast ? 'bg-emerald-600' : 'bg-gray-300'}`}
                  data-testid="a11y-contrast-toggle"
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${highContrast ? 'translate-x-6' : ''} flex items-center justify-center`}
                  >
                    {highContrast && <Check className="w-3 h-3 text-emerald-700" />}
                  </span>
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                    <Contrast className="w-3.5 h-3.5" />
                    Alto contraste
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5">Cores mais fortes, links sublinhados e textos em negrito.</p>
                </div>
              </label>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientA11yButton;
